import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { translate } from 'services/i18nService';
import UserStore from 'stores/UserStore';
import allUserGroups from 'enum/userGroups';
import SomethingWentWrong from 'components/SomethingWentWrong/SomethingWentWrong';
import useEventListener from 'hooks/useEventListener';
import { getAchievementsProfile } from 'pages/Achievements/Api';
import AchievementsModal from 'pages/Achievements/components/AchievementsModal';
import { CardGridUnlocked } from 'pages/Achievements/components/Card';

import styles from './index.css';


const Achievements = ({ profileName }) => {
  const [showModal, setShowModal] = useState(false);
  const [achievementIndex, setAchievementIndex] = useState(0);

  const achievements = useSelector((store) => store.itemsAchievements.achievementsProfile);
  const isError = useSelector((store) => store.itemsAchievements.profileErrors.active);

  const dispatch = useDispatch();

  const reloadAchievement = useCallback(() => {
    dispatch(getAchievementsProfile(profileName));
  }, [dispatch, profileName]);

  const handleNextAchievement = useCallback(() => {
    const nextIndex = achievementIndex + 1;

    if (nextIndex < achievements.length) {
      setAchievementIndex(nextIndex);
    }
  }, [achievementIndex, achievements]);

  const handlePreviousAchievement = useCallback(() => {
    const prevIndex = achievementIndex - 1;

    if (prevIndex >= 0) {
      setAchievementIndex(prevIndex);
    }
  }, [achievementIndex]);

  const openModal = useCallback((index) => {
    setAchievementIndex(index);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setAchievementIndex(0);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      handlePreviousAchievement();
    }

    if (event.key === 'ArrowRight') {
      handleNextAchievement();
    }

    if (event.key === 'Escape') {
      closeModal();
    }
  }, [handlePreviousAchievement, handleNextAchievement, closeModal]);

  useEventListener('keydown', handleKeyDown);

  useEffect(() => {
    if (UserStore.hasCF(allUserGroups.ACHIEVEMENTS_SECTION)) {
      dispatch(getAchievementsProfile(profileName));
    }
  }, [profileName, dispatch]);

  const renderAchievement = useCallback((achievement, index) => {
    const { id, name } = achievement;

    return (
      <div key={id} className={styles.trophiesContent} data-cy="achievements_container">
        <CardGridUnlocked
          id={name}
          index={index}
          onClick={openModal}
        />
      </div>
    );
  }, [openModal]);

  const content = useMemo(() => {
    if (isError) {
      return (
        <div className={styles.trophiesContent} data-cy="achievements_container">
          <SomethingWentWrong
            buttonLabel={translate('account.galleries.tryAgain')}
            text={translate('v2profile.gifts.error')}
            onClick={reloadAchievement}
          />
        </div>
      );
    }

    return (
      <Fragment>
        {achievements?.map(renderAchievement)}
      </Fragment>
    );
  }, [isError, reloadAchievement, achievements, renderAchievement]);

  if (!UserStore.hasCF(allUserGroups.ACHIEVEMENTS_SECTION)) {
    return null;
  }

  return (
    <div className={styles.containerAchievements}>
      <AchievementsModal
        achievement={achievements[achievementIndex]}
        open={showModal}
        performerName={profileName}
        onClose={closeModal}
        onNextPage={handleNextAchievement}
        onPreviousPage={handlePreviousAchievement}
      />

      <div className={styles.containerTitle}>
        <p className={styles.title} data-cy="achievements_section_title">
          {translate('gamification.trophies.profile.achievements.title')}
        </p>
      </div>

      <hr className={styles.delimiter} />

      <div className={styles.containerCard}>
        {content}
      </div>
    </div>
  );
};

Achievements.propTypes = { profileName: PropTypes.string.isRequired };

export default Achievements;
